import React, { useState } from "react";
import { Phone, Globe, Instagram, MapPin, MessageCircle, Mail } from "lucide-react";

export function About({ theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor = theme?.accentColor || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";

  return (
    <div style={{ paddingTop: 64 }}>
      {/* ── Banner — same height as Shop & Contact ── */}
      <div style={{ background: primaryColor, padding: "32px 5vw 28px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ fontSize: "0.68rem", letterSpacing: 4, textTransform: "uppercase", color: accentColor, fontWeight: 700, marginBottom: 6 }}>Our Story</div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#fff" }}>About ACHU Health Mix</h1>
        </div>
      </div>

      <section style={{ padding: "80px 5vw", background: "#fff" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
          <div>
            <div style={{ width: "100%", aspectRatio: "1", borderRadius: "50%", background: `linear-gradient(135deg, #fdf6e3, #f0e8d0)`, border: `3px solid ${accentColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", maxWidth: 360, margin: "auto" }}>
              <div style={{ fontSize: "4rem" }}>🌾</div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: primaryColor, marginTop: 12 }}>ACHU Health Mix</div>
              <div style={{ fontFamily: "cursive", color: "#888", marginTop: 4 }}>We Care for Your Health</div>
              <div style={{ background: secondaryColor, color: "#fff", padding: "4px 16px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700, marginTop: 12 }}>Multi Grain Drink</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "0.72rem", letterSpacing: 4, textTransform: "uppercase", color: secondaryColor, fontWeight: 700, marginBottom: 12 }}>Who We Are</div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "2rem", color: "#2c1810", marginBottom: 20 }}>Nature's Finest in Every Sip</h2>
            <p style={{ color: "#666", lineHeight: 1.8, marginBottom: 20 }}>ACHU Health Mix is a specially hand-crafted multi-grain drink blending 27 carefully selected grains, millets, cereals, pulses, and dry nuts. Created with love and traditional knowledge, it delivers superior nutrition for every member of your family.</p>
            <p style={{ color: "#666", lineHeight: 1.8, marginBottom: 28 }}>Every batch is hand-made with zero added sugar, zero artificial colours, zero preservatives. Just pure, wholesome goodness.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[["Zero Sugar", "Pure natural sweetness"], ["Zero Additives", "No artificial anything"], ["Hand Made", "Every batch with care"], ["Daily Nutrition", "For all age groups"]].map(([t, d]) => (
                <div key={t} style={{ background: "#fdf6e3", padding: 16, borderRadius: 12, borderLeft: `3px solid ${secondaryColor}` }}>
                  <div style={{ fontWeight: 700, color: primaryColor, fontSize: "0.88rem", marginBottom: 4 }}>{t}</div>
                  <div style={{ fontSize: "0.78rem", color: "#888" }}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: "80px 5vw", background: "#fdf6e3" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "2rem", color: "#2c1810", marginBottom: 48 }}>27 Hand-Selected Grains & Millets</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
            {["Wheat","Rice","Ragi","Bajra","Jowar","Corn","Oats","Barley","Chickpeas","Green Gram","Black Gram","Horse Gram","Lentils","Soybean","Groundnuts","Almonds","Cashews","Cardamom","Dry Ginger","Turmeric","Flaxseeds","Sesame","Dates","Fenugreek","Cumin","Pepper","Dry Coconut"].map(g => (
              <span key={g} style={{ background: "#fff", border: `1px solid ${accentColor}60`, padding: "6px 16px", borderRadius: 40, fontSize: "0.82rem", color: "#5a3825" }}>{g}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function Contact({ theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor = theme?.accentColor || "#c9a84c";
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  return (
    <div style={{ paddingTop: 64 }}>
      {/* ── Banner — same height as Shop & About ── */}
      <div style={{ background: primaryColor, padding: "32px 5vw 28px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ fontSize: "0.68rem", letterSpacing: 4, textTransform: "uppercase", color: accentColor, fontWeight: 700, marginBottom: 6 }}>Support</div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#fff" }}>Contact Us</h1>
        </div>
      </div>

      <section style={{ padding: "80px 5vw", background: "#fdf6e3" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64 }}>
          <div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.8rem", color: "#2c1810", marginBottom: 28 }}>Get In Touch</h2>
            {[
              { icon: <Phone size={18}/>, label: "Primary", val: "98403 20932", href: "tel:+919840320932" },
              { icon: <Phone size={18}/>, label: "Secondary", val: "96590 01122", href: "tel:+919659001122" },
              { icon: <MessageCircle size={18}/>, label: "WhatsApp", val: "Chat with us", href: "https://wa.me/919840320932" },
              { icon: <Globe size={18}/>, label: "Website", val: "achuhealthmix.com", href: "https://www.achuhealthmix.com" },
              { icon: <Instagram size={18}/>, label: "Instagram", val: "@achu.healthmix", href: "https://instagram.com/achu.healthmix" },
            ].map(c => (
              <a key={c.val} href={c.href} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, textDecoration: "none" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fff", border: `2px solid ${accentColor}40`, display: "flex", alignItems: "center", justifyContent: "center", color: primaryColor, flexShrink: 0 }}>{c.icon}</div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#999", marginBottom: 2 }}>{c.label}</div>
                  <div style={{ fontWeight: 700, color: "#2c1810" }}>{c.val}</div>
                </div>
              </a>
            ))}
          </div>

          <div style={{ background: "#fff", padding: 36, borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: "#2c1810", marginBottom: 24 }}>Send a Message</h3>
            {sent ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: "3rem", marginBottom: 16 }}>✅</div>
                <h3 style={{ color: "#388e3c", fontFamily: "Georgia, serif" }}>Message Sent!</h3>
                <p style={{ color: "#888", marginTop: 8 }}>We'll get back to you shortly.</p>
              </div>
            ) : (
              <div>
                {[
                  { name: "name", label: "Your Name", placeholder: "Full name" },
                  { name: "email", label: "Email", placeholder: "your@email.com", type: "email" },
                  { name: "phone", label: "Phone", placeholder: "+91 98XXX XXXXX" },
                ].map(f => (
                  <div key={f.name} style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, color: primaryColor, marginBottom: 6 }}>{f.label}</label>
                    <input type={f.type || "text"} value={form[f.name]} onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))} placeholder={f.placeholder}
                      style={{ width: "100%", padding: "11px 14px", border: "2px solid rgba(107,33,24,0.15)", borderRadius: 10, fontSize: "0.9rem", outline: "none", fontFamily: "inherit" }} />
                  </div>
                ))}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, color: primaryColor, marginBottom: 6 }}>Message</label>
                  <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={4} placeholder="Your message..." style={{ width: "100%", padding: "11px 14px", border: "2px solid rgba(107,33,24,0.15)", borderRadius: 10, fontSize: "0.9rem", outline: "none", resize: "vertical", fontFamily: "inherit" }} />
                </div>
                <button onClick={() => setSent(true)} style={{ width: "100%", padding: 13, background: primaryColor, color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: "1rem", cursor: "pointer" }}>Send Message</button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
